// pages/AdminDashboard/components/SessionsManagement.jsx
import React, { useState, useEffect } from "react";
import styled from "styled-components";
import {
	collection,
	getDocs,
	getDoc,
	doc,
	orderBy,
	query,
	Timestamp,
	updateDoc,
} from "firebase/firestore";
import { db } from "../../../config/firebase";
import { Button } from "../../../styles/components";
import LoadingScreen from "../../../components/common/loading/LoadingScreen";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";

const TableContainer = styled.div`
	overflow-x: auto;
	background: white;
	border-radius: 8px;
	box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
`;

const Table = styled.table`
	width: 100%;
	border-collapse: collapse;

	th,
	td {
		padding: 12px;
		text-align: left;
		border-bottom: 1px solid #eee;
	}

	th {
		background: #f9fafb;
		font-weight: 600;
	}
`;

const Status = styled.span`
	padding: 4px 8px;
	border-radius: 4px;
	font-size: 14px;
	font-weight: 500;
	background: ${(props) =>
		props.status === "pending"
			? "#fff7ed"
			: props.status === "completed"
			? "#f0fdf4"
			: "#fef2f2"};
	color: ${(props) =>
		props.status === "pending"
			? "#ea580c"
			: props.status === "completed"
			? "#16a34a"
			: "#dc2626"};
`;

const Modal = styled.div`
	position: fixed;
	top: 0;
	left: 0;
	width: 100%;
	height: 100%;
	background: rgba(0, 0, 0, 0.5);
	display: flex;
	justify-content: center;
	align-items: center;
`;

const ModalContent = styled.div`
	background: white;
	padding: 2rem;
	border-radius: 8px;
	max-width: 500px;
	width: 90%;
`;

const FormGroup = styled.div`
	margin-bottom: 1rem;
`;

const Label = styled.label`
	display: block;
	margin-bottom: 0.5rem;
	font-weight: 500;
`;

const Select = styled.select`
	width: 100%;
	padding: 0.5rem;
	border: 1px solid #e2e8f0;
	border-radius: 0.375rem;
	margin-bottom: 1rem;
`;

const ButtonGroup = styled.div`
	display: flex;
	gap: 1rem;
	margin-top: 1.5rem;
`;

const SessionsManagement = () => {
	const [sessions, setSessions] = useState([]);
	const [loading, setLoading] = useState(true);
	const [editSession, setEditSession] = useState(null);
	const [showEditModal, setShowEditModal] = useState(false);

	const handleEdit = async (session) => {
		setEditSession(session);
		setShowEditModal(true);
	};

	const handleUpdate = async (updatedData) => {
		try {
			await updateDoc(doc(db, "sessions", editSession.id), {
				...updatedData,
				date: Timestamp.fromDate(new Date(updatedData.date)),
				updatedAt: Timestamp.now(),
			});
			setShowEditModal(false);
			// Refresh sessions
			fetchSessions();
		} catch (error) {
			console.error("Error updating session:", error);
		}
	};


	const fetchSessions = async () => {
		try {
			const q = query(collection(db, "sessions"), orderBy("date", "desc"));
			const snapshot = await getDocs(q);

			console.log(snapshot);

			// Get unique user IDs from sessions
			const userIds = [
				...new Set(snapshot.docs.map((doc) => doc.data().userId)),
			];

			// Fetch only needed users
			const userSnapshots = await Promise.all(
				userIds.map((id) => getDoc(doc(db, "users", id)))
			);
			const userNames = Object.fromEntries(
				userSnapshots.map((snap) => [snap.id, snap.data()?.name])
			);

			const sessionsData = snapshot.docs.map((doc) => ({
				id: doc.id,
				...doc.data(),
				date: doc.data().date?.toDate(),
				clientName: userNames[doc.data().userId] || "Unknown",
			}));

			setSessions(sessionsData);
		} catch (error) {
			console.error("Error fetching sessions:", error);
		} finally {
			setLoading(false);
		}
	};

	useEffect(() => {
		fetchSessions();
	}, []);

	if (loading) return <LoadingScreen />;

	return (
		<TableContainer>
			<Table>
				<thead>
					<tr>
						<th>Client</th>
						<th>Date</th>
						<th>Time</th>
						<th>Type</th>
						<th>Status</th>
						<th>Actions</th>
					</tr>
				</thead>
				<tbody>
					{sessions.map((session) => (
						<tr key={session.id}>
							<td>{session.clientName}</td>
							<td>{new Date(session.date).toLocaleDateString()}</td>
							<td>{session.time}</td>
							<td>{session.type}</td>
							<td>
								<Status status={session.status}>
									{session.status.toUpperCase()}
								</Status>
							</td>
							<td>
								<Button
									onClick={() => handleEdit(session)}
									variant="outlined"
									size="small"
									style={{ marginRight: "8px" }}
								>
									Edit
								</Button>
							</td>
						</tr>
					))}

					{sessions.length === 0 && (
						<tr>
							<td colSpan="6" style={{ textAlign: "center", padding: "24px" }}>
								No sessions found
							</td>
						</tr>
					)}
				</tbody>
			</Table>

			{showEditModal && (
				<EditSessionModal
					session={editSession}
					onClose={() => setShowEditModal(false)}
					onSubmit={handleUpdate}
				/>
			)}
		</TableContainer>
	);
};

export default SessionsManagement;

const EditSessionModal = ({ session, onClose, onSubmit }) => {
	const [formData, setFormData] = useState({
		date: session.date,
		timeSlot: session.timeSlot,
		type: session.type,
		status: session.status,
	});

	const handleSubmit = (e) => {
		e.preventDefault();
		onSubmit(formData);
	};

	return (
		<Modal onClick={onClose}>
			<ModalContent onClick={(e) => e.stopPropagation()}>
				<form onSubmit={handleSubmit}>
					<FormGroup>
						<Label>Date</Label>
						<DatePicker
							selected={formData.date}
							onChange={(date) => setFormData({ ...formData, date })}
							className="date-picker"
						/>
					</FormGroup>

					<FormGroup>
						<Label>Time</Label>
						<Select
							value={formData.timeSlot}
							onChange={(e) =>
								setFormData({ ...formData, timeSlot: e.target.value })
							}
						>
							<option value="09:00">09:00</option>
							<option value="13:00">13:00</option>
							<option value="17:00">17:00</option>
						</Select>
					</FormGroup>

					<FormGroup>
						<Label>Status</Label>
						<Select
							value={formData.status}
							onChange={(e) =>
								setFormData({ ...formData, status: e.target.value })
							}
						>
							<option value="pending">Pending</option>
							<option value="confirmed">Confirmed</option>
							<option value="cancelled">Cancelled</option>
						</Select>
					</FormGroup>

					<ButtonGroup>
						<Button type="submit" variant="primary">
							Update
						</Button>
						<Button type="button" variant="outlined" onClick={onClose}>
							Cancel
						</Button>
					</ButtonGroup>
				</form>
			</ModalContent>
		</Modal>
	);
};
